// src/components/Admin/QuestionsAnswered.jsx
import React, { useState, useEffect } from "react";
import {
  CheckCircle,
  BookOpen,
  User,
  Calendar,
  Tag,
  Star,
  ChevronRight,
  Search,
  Download,
} from "lucide-react";

import API_BASE_URL from "../../services/config";

import { authFetch } from "../../auth";

const QuestionsAnswered = () => {
  const [questions, setQuestions] = useState([]);
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [dateRange, setDateRange] = useState("all");
  const [expandedQuestion, setExpandedQuestion] = useState(null);
  const [error, setError] = useState("");

  const categories = [
    "All",
    "Daily Life & Dharma",
    "Relationships & Family",
    "Career & Duty",
    "Festivals & Rituals",
    "Philosophy & Spiritual Growth",
  ];

  useEffect(() => {
    fetchAnsweredQuestions();
  }, []);

  useEffect(() => {
    filterQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [questions, searchTerm, selectedCategory, dateRange]);

  const fetchAnsweredQuestions = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await authFetch(
        `${API_BASE_URL}/api/admin/questions/answered`,
        { method: "GET" }
      );

      if (!res.ok) {
        throw new Error(`Failed to load answered questions (${res.status})`);
      }

      const data = await res.json();
      setQuestions(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error fetching answered questions:", err);
      setError(err.message || "Failed to load answered questions.");
      setQuestions([]);
    } finally {
      setLoading(false);
    }
  };

  const filterQuestions = () => {
    let filtered = [...questions];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter((q) => {
        const title = q.title || "";
        const body = q.body || "";
        const answer = q.answer || "";
        return (
          title.toLowerCase().includes(term) ||
          body.toLowerCase().includes(term) ||
          answer.toLowerCase().includes(term)
        );
      });
    }

    // Category filter
    if (selectedCategory !== "All") {
      filtered = filtered.filter((q) => q.category === selectedCategory);
    }

    // Date filter
    if (dateRange !== "all") {
      const now = new Date();
      const cutoff = new Date();

      switch (dateRange) {
        case "today":
          cutoff.setDate(now.getDate() - 1);
          break;
        case "week":
          cutoff.setDate(now.getDate() - 7);
          break;
        case "month":
          cutoff.setMonth(now.getMonth() - 1);
          break;
        case "quarter":
          cutoff.setMonth(now.getMonth() - 3);
          break;
        default:
          break;
      }

      filtered = filtered.filter((q) => {
        if (!q.answeredAt) return false;
        const d = new Date(q.answeredAt);
        return !isNaN(d.getTime()) && d > cutoff;
      });
    }

    setFilteredQuestions(filtered);
  };

  const getCategoryColor = (category) => {
    const colors = {
      "Daily Life & Dharma": "from-blue-500 to-cyan-500",
      "Relationships & Family": "from-pink-500 to-rose-500",
      "Career & Duty": "from-indigo-500 to-purple-500",
      "Festivals & Rituals": "from-amber-500 to-orange-500",
      "Philosophy & Spiritual Growth": "from-emerald-500 to-teal-500",
    };
    return colors[category] || "from-gray-500 to-gray-600";
  };

  const getRatingStars = (rating) =>
    Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          className={`w-4 h-4 ${
            i < rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"
          }`}
        />
      ));

  const formatDate = (value, options) => {
    if (!value) return "Unknown";
    const d = new Date(value);
    if (isNaN(d.getTime())) return "Unknown";
    return d.toLocaleDateString("en-US", options);
  };

  const exportToCSV = () => {
    const header = [
      "Title",
      "Category",
      "User",
      "Answered Date",
      "Rating",
      "Answer Length",
    ];

    const rows = filteredQuestions.map((q) => [
      `"${(q.title || "").replace(/"/g, '""')}"`,
      `"${(q.category || "").replace(/"/g, '""')}"`,
      `"${(q.user?.name || "Anonymous").replace(/"/g, '""')}"`,
      formatDate(q.answeredAt, {
        year: "numeric",
        month: "short",
        day: "numeric",
      }),
      q.rating || "No rating",
      (q.answer || "").length,
    ]);

    const csvContent = [header, ...rows].map((r) => r.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `answered-questions-${
      new Date().toISOString().split("T")[0]
    }.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96 space-y-4">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
        <p className="text-blue-600 font-medium">
          Loading answered questions...
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <CheckCircle className="h-8 w-8" />
              <h1 className="text-2xl font-bold">Answered Questions</h1>
            </div>
            <p className="text-blue-100">
              Questions that have been answered with scriptural guidance
            </p>
          </div>
          <div className="text-right">
            <div className="text-3xl font-bold">{questions.length}</div>
            <p className="text-blue-100 text-sm">Total Answered</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search questions..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Category Filter */}
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>

          {/* Date Range */}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="quarter">This Quarter</option>
          </select>

          {/* Export Button */}
          <button
            onClick={exportToCSV}
            className="flex items-center justify-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-4 py-2 rounded-lg hover:opacity-90 transition-opacity"
          >
            <Download className="h-5 w-5" />
            <span>Export CSV</span>
          </button>
        </div>

        {error && (
          <div className="mt-4 rounded-md bg-red-50 p-3 text-sm text-red-700 border border-red-200">
            {error}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <BookOpen className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Answer Length</p>
              <p className="text-xl font-bold">
                {filteredQuestions.length > 0
                  ? Math.round(
                      filteredQuestions.reduce(
                        (acc, q) => acc + (q.answer?.length || 0),
                        0
                      ) / filteredQuestions.length
                    )
                  : 0}{" "}
                chars
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-yellow-100 p-2 rounded-lg">
              <Star className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg Rating</p>
              <p className="text-xl font-bold">
                {filteredQuestions.filter((q) => q.rating).length > 0
                  ? (
                      filteredQuestions
                        .filter((q) => q.rating)
                        .reduce((acc, q) => acc + q.rating, 0) /
                      filteredQuestions.filter((q) => q.rating).length
                    ).toFixed(1)
                  : "N/A"}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-2 rounded-lg">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Recently Answered</p>
              <p className="text-xl font-bold">
                {
                  questions.filter((q) => {
                    if (!q.answeredAt) return false;
                    const weekAgo = new Date();
                    weekAgo.setDate(weekAgo.getDate() - 7);
                    const d = new Date(q.answeredAt);
                    return !isNaN(d.getTime()) && d > weekAgo;
                  }).length
                }
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <div className="flex items-center space-x-3">
            <div className="bg-purple-100 p-2 rounded-lg">
              <User className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Users</p>
              <p className="text-xl font-bold">
                {
                  new Set(
                    questions.map((q) => q.user?.id).filter(Boolean)
                  ).size
                }
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Questions List */}
      <div className="space-y-4">
        {filteredQuestions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl border border-gray-200">
            <BookOpen className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              No answered questions found
            </h3>
            <p className="text-gray-600">
              Try adjusting your filters or check back later
            </p>
          </div>
        ) : (
          filteredQuestions.map((question) => (
            <div
              key={question.id}
              className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
            >
              <div className="p-6">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    {/* Question Header */}
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <div className="flex items-center space-x-3 mb-2">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-gradient-to-r ${getCategoryColor(
                              question.category
                            )} text-white`}
                          >
                            {question.category || "Uncategorized"}
                          </span>
                          {question.rating && (
                            <div className="flex items-center space-x-1">
                              {getRatingStars(question.rating)}
                            </div>
                          )}
                        </div>
                        <h3 className="text-lg font-bold text-gray-900 mb-2">
                          {question.title}
                        </h3>
                      </div>
                      <button
                        onClick={() =>
                          setExpandedQuestion(
                            expandedQuestion === question.id
                              ? null
                              : question.id
                          )
                        }
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <ChevronRight
                          className={`h-5 w-5 transition-transform ${
                            expandedQuestion === question.id
                              ? "rotate-90"
                              : ""
                          }`}
                        />
                      </button>
                    </div>

                    {/* Question Body */}
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {question.body}
                    </p>

                    {/* Meta Information */}
                    <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                      <span className="flex items-center">
                        <User className="h-4 w-4 mr-1" />
                        {question.user?.name || "Anonymous"}
                      </span>
                      <span className="flex items-center">
                        <Calendar className="h-4 w-4 mr-1" />
                        Answered:{" "}
                        {formatDate(question.answeredAt, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })}
                      </span>
                      <span className="text-sm">
                        Answer length: {question.answer?.length || 0} characters
                      </span>
                    </div>

                    {/* Expanded Answer */}
                    {expandedQuestion === question.id && question.answer && (
                      <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                        <h4 className="font-bold text-blue-800 mb-3 flex items-center">
                          <CheckCircle className="h-5 w-5 mr-2" />
                          Scriptural Answer
                        </h4>
                        <div className="prose prose-blue max-w-none">
                          <div className="whitespace-pre-wrap text-gray-700">
                            {question.answer}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default QuestionsAnswered;
